import type { Extensions } from '@tiptap/core'
import type { EditorAdapter } from './adapter'
import { IssueRef } from '../extensions/issue-reference'
import { IssueSuggestion } from '../extensions/issue-suggestion'
import { MacroSuggestion, RedmineMacroBlock, RedmineMacroInline } from '../extensions/redmine-macro'
import { ResourceRef } from '../extensions/resource-ref'
import { UserMention } from '../extensions/user-mention'
import { WikiLink, WikiLinkSuggestion } from '../extensions/wiki-link'
import { rmDataSources } from '../utils/redmine'

export class WikiAdapter implements EditorAdapter {
  readonly context = 'wiki' as const
  readonly textareaSelector = '#content_text'
  readonly attachmentsContainerSelector = '.attachments_fields'

  constructor(private readonly meta: HTMLElement) { }

  get previewUrl(): string | undefined {
    return this.meta.dataset.previewUrl || undefined
  }

  get uploadPath(): string {
    return document.querySelector<HTMLInputElement>('input.filedrop')
      ?.dataset
      .uploadPath ?? '/uploads.js'
  }

  get userMentionUrl(): string | null {
    return this.meta.dataset.userMentionUrl
      || rmDataSources()?.users
      || null
  }

  get issueSuggestionUrl(): string | null {
    return rmDataSources()?.issues || null
  }

  get wikiPageSuggestionUrl(): string | null {
    // Build URL from the meta span's project context if available, else null
    const mentionUrl = this.userMentionUrl
    if (!mentionUrl)
      return null
    // Derive project_id from the mention URL pattern: ...?project_id=X&...
    const m = /[?&]project_id=([^&]+)/.exec(mentionUrl)
    if (!m)
      return null
    return `/wiki_pages/auto_complete?project_id=${m[1]}&q=`
  }

  get extensions(): Extensions {
    const exts: Extensions = [WikiLink, IssueRef, RedmineMacroInline, RedmineMacroBlock, ResourceRef, MacroSuggestion]
    if (this.issueSuggestionUrl)
      exts.push(IssueSuggestion.configure({ url: this.issueSuggestionUrl }))
    if (this.wikiPageSuggestionUrl)
      exts.push(WikiLinkSuggestion.configure({ url: this.wikiPageSuggestionUrl }))
    if (this.userMentionUrl)
      exts.push(UserMention.configure({ url: this.userMentionUrl }))
    return exts
  }
}
